<template>
    <LoadingCard v-if="loading && !hasLoaded"></LoadingCard>

    <div class="card" v-else>
        <div class="bg-primary align-items-center d-flex">
            <h5 class="card-header text-white">Blocks with a Final Spray</h5>
            <b-pagination align="right" class="my-auto mr-2 ml-auto"
                          :total-rows="pagination.total"
                          v-model="pagination.page"
                          :per-page="pagination.limit" />
        </div>

        <div class="form-control form-control-lg bg-light form-filter d-flex">
            <VueSelect
                    v-model="cropType"
                    :options="allCropTypes"
                    placeholder="Filter by CropType"
                    label="name"
                    track-by="id"
            ></VueSelect>
            <VueSelect
                    v-model="variety"
                    :options="allVarieties"
                    placeholder="Filter by Variety"
                    label="name"
                    track-by="id"
            ></VueSelect>
        </div>

        <b-table hover stacked="md"
                 :items="tableData"
                 :fields="blocksTable.fields"
                 :show-empty="true"
                 :sort-by.sync="blocksTable.sortBy"
                 :sort-desc.sync="blocksTable.sortDesc"
                 :no-local-sorting="true"
                 :busy="this.loading > 0">

            <template slot="cropType" slot-scope="row">
                <span v-if="row.item.cropType">{{ row.item.cropType.name }}</span>
                <span class="badge badge-danger" v-else>Removed</span>
            </template>

            <template slot="variety" slot-scope="row">
                <span v-if="row.item.cropType && row.item.variety">{{ row.item.variety.name }}</span>
                <span class="badge badge-danger" v-else-if="row.item.cropType">Removed</span>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="withholdingPeriods" slot-scope="row">
                <template v-if="row.item.currentWithholdingPeriods.length > 0">
                    <span class="badge-group mr-2" v-for="period in activeWithholdingPeriods(row.item)">
                        <span class="badge badge-secondary mb-1">{{ period.country.name }} ({{ period.length }} days)</span>
                        <span class="badge mb-1" :class="{'badge-success': period.is_past, 'badge-danger': !period.is_past}">{{ period.ends_at | date_format }}</span>
                    </span>
                </template>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="restrictedEntryInterval" slot-scope="row">
                <strong class="text-danger" v-if="row.item.restrictedEntryInterval">{{ row.item.restrictedEntryInterval.ends_at | date_format
                                                                                    }}</strong>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="final_spray" slot-scope="row">
                <span class="badge badge-pill badge-success" v-if="row.item.final_spray">Yes</span>
                <span class="badge badge-pill badge-light" v-else>No</span>
            </template>

            <template slot="actions" slot-scope="row">
                <b-button :to="{name: 'spray-diary.view', params: {blockId: row.item.id}}"
                          variant="secondary btn--icon"
                          v-b-tooltip="'View spray diary'">
                    <i class="zmdi zmdi-view-list-alt"></i>
                </b-button>
            </template>

        </b-table>

    </div>

</template>

<script>
import listBlocksQuery from '@views/FinalSprayBlocks/ProcessorBrowse/queryListBlocks.graphql';
import listCropTypesQuery from '@views/FinalSprayBlocks/ProcessorBrowse/queryListCropTypes.graphql';
import listVarietiesQuery from '@views/FinalSprayBlocks/ProcessorBrowse/queryListVarieties.graphql';
import isAfter from 'date-fns/is_after';

export default {
  name: 'ProcessorFinalSprayBlocksBrowse',
  props: ['cropTypeId'],
  components: {},
  apollo: {
    blocks: {
      query: listBlocksQuery,
      variables() {
        const variables = {
          input: {
            crop_type_id: this.cropTypeId,
            page: this.pagination.page,
            limit: this.pagination.limit,
            final_spray: true,
            sort: {
              direction: this.blocksTable.sortDesc
                ? 'DESC'
                : 'ASC',
              field: this.blocksTable.sortBy,
            },
          },
        };
        if (this.variety) {
          variables.input.variety_id = this.variety.id;
        }
        if (this.cropType) {
          variables.input.crop_type_id = this.cropType.id;
        }

        return variables;
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.blocks = data.filterBlocks.data;
          this.pagination.page = data.filterBlocks.meta.page;
          this.pagination.limit = data.filterBlocks.meta.limit;
          this.pagination.total = data.filterBlocks.meta.total;
          this.pagination.from = data.filterBlocks.meta.from;
          this.pagination.to = data.filterBlocks.meta.to;
        }
      },
    },
    allCropTypes: {
      query: listCropTypesQuery,
      update: data => {
        return data.filterCropTypes.data;
      },
      variables() {
        return {
          input: {
            limit: 1000,
          },
        };
      },
    },
    allVarieties: {
      query: listVarietiesQuery,
      update: data => {
        return data.filterVarieties.data;
      },
      variables() {
        return {
          input: {
            limit: 1000,
          },
        };
      },
    },
  },
  watch: {
    loading(newValue) {
      if (!this.hasLoaded && newValue === 0) {
        this.hasLoaded = true;
      }
    },
  },
  data() {
    return {
      loading: 0,
      hasLoaded: false,
      blocks: [],
      allCropTypes: [],
      allVarieties: [],
      variety: null,
      cropType: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        from: null,
        to: null,
      },
      blocksTable: {
        sortBy: 'name',
        sortDesc: false,
        fields: [
          { key: 'name', label: 'Block Name', sortable: true },
          { key: 'withholdingPeriods', label: 'Withholding Periods' },
          { key: 'restrictedEntryInterval', label: 'REI' },
          {
            key: 'cropType',
            label: 'Crop Type',
            sortable: true,
          },
          {
            key: 'variety',
            label: 'Crop Variety',
            sortable: true,
            formatter: variety => variety.name,
          },
          { key: 'final_spray', label: 'Final Spray' },
          { key: 'actions', label: '', class: 'text-right' },
        ],
      },
    };
  },
  methods: {
    activeWithholdingPeriods(item) {
      const byCountry = {};
      item.currentWithholdingPeriods.forEach(period => {
        if (!(period.country.id in byCountry) || isAfter(period.ends_at, byCountry[period.country.id].ends_at)) {
          byCountry[period.country.id] = period;
        }
      });

      return Object.values(byCountry);
    },
  },
  computed: {
    tableData() {
      return (this.blocks || []).map(item => ({
        ...item,
        _cellVariants: {restrictedEntryInterval: item.restrictedEntryInterval ? 'danger' : null},
        // _rowVariant: item.restrictedEntryInterval ? 'danger' : null,
      }));
    },
  },
};
</script>
